import React, { useState } from 'react';
import { useIsMobile } from '../../../hooks/useIsMobile';
import { SignaturePad } from '../../common/SignaturePad';
import { UNTERSCHRIFT_MAX_BREITE, istTouchGeraet } from '../../sifa/public/teilnahmeSeiteLogic';
import { TeilnahmeRahmen } from '../../sifa/public/TeilnahmeRahmen';
import { FEHLERKASTEN, HINWEISKASTEN } from '../../sifa/public/teilnahmeSeiteStil';
import { useEinweisungTeilnahme } from './useEinweisungTeilnahme';
import { UNTERSCHRIFT_HINWEIS, UNTERSCHRIFT_TITEL, fortschrittProzent, schrittText } from './einweisungTeilnahmeLogic';
import { EinweisungBegruessung } from './EinweisungBegruessung';
import { EinweisungInhalt } from './EinweisungInhalt';
import { EinweisungFormular } from './EinweisungFormular';
import { EinweisungUnterschrift } from './EinweisungUnterschrift';
import { EinweisungErgebnis } from './EinweisungErgebnis';

/**
 * Oeffentliche Teilnehmerseite #/einweisung/<linkToken> — Einweisung in den
 * SiGe-Plan per Link/QR (docs/sige-einweisung-online-plan.md §4).
 *
 * Wird OHNE Login gerendert (index.tsx routet am Auth-Gate vorbei, wie
 * #/unterweisung). Duenner Container: der Zustand lebt in
 * useEinweisungTeilnahme, die Regeln in einweisungTeilnahmeLogic; hier wird
 * nur der aktuelle Schritt auf die passende Ansicht abgebildet.
 */
export const EinweisungTeilnahmePage: React.FC<{ linkToken: string }> = ({ linkToken }) => {
  const t = useEinweisungTeilnahme(linkToken);
  const schmal = useIsMobile();
  const [groberZeiger] = useState(() => {
    try {
      return window.matchMedia('(pointer: coarse)').matches;
    } catch {
      return false;
    }
  });
  const touch = istTouchGeraet(groberZeiger, schmal);

  if (t.ladeStatus === 'laedt') {
    return (
      <TeilnahmeRahmen>
        <p className="text-slate-500" aria-live="polite">
          Einweisung wird geladen…
        </p>
      </TeilnahmeRahmen>
    );
  }

  // Keine Angabe, ob es die Einweisung gibt — der Text ist fuer unbekannten,
  // abgelaufenen und deaktivierten Link derselbe.
  if (t.ladeStatus === 'ungueltig' || !t.einweisung) {
    return (
      <TeilnahmeRahmen>
        <h1 className="text-xl font-bold text-slate-900">Dieser Link ist nicht mehr gültig</h1>
        <div className={HINWEISKASTEN}>Dieser Link ist nicht mehr gültig — bitte an den SiGe-Koordinator wenden.</div>
      </TeilnahmeRahmen>
    );
  }

  if (t.ladeStatus === 'fehler') {
    return (
      <TeilnahmeRahmen>
        <h1 className="text-xl font-bold text-slate-900">Die Einweisung konnte nicht geladen werden</h1>
        <div className={FEHLERKASTEN}>{t.ladeFehler}</div>
        <p className="text-slate-600">Bitte prüfe die Internetverbindung und lade die Seite neu.</p>
      </TeilnahmeRahmen>
    );
  }

  const e = t.einweisung;
  const { schritt, zustand } = t;

  // Smartphone: das Unterschriftenfeld ist die ganze Seite (wie UnterweisungTeilnahmePage).
  if (schritt === 'unterschrift' && touch) {
    return (
      <SignaturePad
        titel={UNTERSCHRIFT_TITEL}
        hinweis={UNTERSCHRIFT_HINWEIS}
        maxBreite={UNTERSCHRIFT_MAX_BREITE}
        uebernehmenLabel="Unterschrift übernehmen"
        busy={t.busy}
        fehler={t.fehler}
        onUebernehmen={(dataUrl) => void t.abschliessen(dataUrl)}
        onAbbrechen={t.zurueck}
        abbrechenLabel="Zurück"
      />
    );
  }

  let inhalt: React.ReactNode;
  switch (schritt) {
    case 'begruessung':
      inhalt = <EinweisungBegruessung einweisung={e} onLesen={t.weiter} />;
      break;
    case 'inhalt':
      // Der Schrittwechsel haengt die Ansicht aus und wieder ein — Lesezeit
      // und Freigabe beginnen fuer jede Person neu.
      inhalt = <EinweisungInhalt gruppen={e.punkte} onWeiter={t.weiter} onZurueck={t.zurueck} />;
      break;
    case 'formular':
      inhalt = <EinweisungFormular formular={zustand.formular} onAendern={t.formularAendern} onWeiter={t.weiter} onZurueck={t.zurueck} />;
      break;
    case 'unterschrift':
      inhalt = (
        <EinweisungUnterschrift
          busy={t.busy}
          fehler={t.fehler}
          onUnterschrift={(dataUrl) => void t.abschliessen(dataUrl)}
          onZurueck={t.zurueck}
        />
      );
      break;
    case 'ergebnis':
      inhalt = zustand.ergebnis ? (
        <EinweisungErgebnis ergebnis={zustand.ergebnis} onNaechstePerson={t.naechstePerson} />
      ) : (
        <EinweisungFormular formular={zustand.formular} onAendern={t.formularAendern} onWeiter={t.weiter} onZurueck={t.zurueck} />
      );
      break;
  }

  return (
    <TeilnahmeRahmen prozent={fortschrittProzent(schritt)} schrittText={schrittText(schritt)}>
      {inhalt}
    </TeilnahmeRahmen>
  );
};
